import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import Flexbox from 'flexbox-react';
import { css } from 'emotion';
import AbstractLocalStorageComponent from 'components/core/AbstractLocalStorageComponent';
import Toolbar from 'components/core/toolbar/Toolbar';
import ToolbarItem from 'components/core/toolbar/ToolbarItem';
import ToolbarSeparator from 'components/core/toolbar/ToolbarSeparator';
import ProcessService from 'services/ProcessService';
import DodService from 'services/DodService';
import SecurityService from 'services/SecurityService';
import SiteFacilitiesService from 'services/SiteFacilitiesService';
import RecommendedDataService from 'services/RecommendedDataService';
import ModalsService from 'services/ModalsService';
import DataExplorerService from 'services/DataExplorerService';
import ProcessUtils from 'utils/ProcessUtils';
import NavigationUtils from 'utils/NavigationUtils';
import ImportVariablesWizard from 'components/page/process/variableRetrieval/ImportVariablesWizard';
import { Switch, Route } from 'react-router-dom';
import ProcessWizard from 'components/page/process/wizard/ProcessWizard.js';
import ProcessForm from 'components/page/process/ProcessForm';
import ProcessTextEditor from 'components/page/process/ProcessTextEditor';
import RevisionHistory from 'components/page/process/RevisionHistory';
import InputsOutputsForm from 'components/page/process/InputsOutputsForm';
import RunCodeForm from 'components/page/process/RunCodeForm';
import CreateCodeForm from 'components/page/process/CreateCodeForm';
import VariableRetrieval from 'components/page/process/variableRetrieval/VariableRetrieval';
import PageDoesNotExist from 'components/core/404';
import RollupError from 'components/core/RollupError';
import { Menu, Dropdown, Icon, Popover } from 'antd';
import { popoverErrorStyle, colorLightGrey, colorVeryLightGrey } from 'styles/base';

const MenuItemGroup = Menu.ItemGroup;


/**
 * The Process components renders details about a given process.
 */
class Process extends AbstractLocalStorageComponent {
    static propTypes = {
        location: PropTypes.object,
        process: PropTypes.object,
        processErrors: PropTypes.object,
        processRevisions: PropTypes.array,
        exploredDatastreams: PropTypes.object,
        children: PropTypes.object,
        dispatch: PropTypes.func,
        hasWritePermissions: PropTypes.bool,
        hasSavePermissions: PropTypes.bool, //for saving process revision
        userLoggedIn: PropTypes.bool,
        processIsDirty: PropTypes.bool,
        siteFacilities: PropTypes.array,
        recommendedVariables: PropTypes.array,
        dods: PropTypes.array,
        dodsById: PropTypes.object,
        showImportWizard: PropTypes.bool,
        processId: PropTypes.string,
        revisionNum: PropTypes.string,
        mode: PropTypes.string,
        processUIState: PropTypes.object
    };

    componentWillMount() {
        // Make sure supporting information is loaded in the background for use in typeaheads
        SiteFacilitiesService.fetchSiteFacilitiesIfNeeded();
        RecommendedDataService.fetchRecommendedVariablesIfNeeded();
        DodService.fetchDodsIfNeeded();

        // Get the process id from the route
        const processId = this.props.processId;
        const revisionNum = this.props.revisionNum;

        // Make sure the processes are loaded (we need this to check for duplicate process names)
        ProcessService.fetchProcessesIfNeeded();

        if (revisionNum) {
            ProcessService.fetchProcessRevisionIfNeeded(processId, revisionNum);
        } else {
            const mode = this.getModeFromPath(this.props);
            ProcessService.fetchProcessIfNeeded(processId, { mode });
        }

        const pathMode = this.getModeFromPath(this.props);
        if (this.props.mode !== null && this.props.mode !== pathMode) {
            ProcessService.updateProcessUIState(this.props.processId, { mode: pathMode });
        }
    }

    /**
     * We are implementing this method so we don't update unless the props actually change.
     * Apparently there is no superclass version of this method, and React always assumes
     * true, which was causing the Process component to rerender everytime there was a
     * redux store change, no matter if the Process props actually changed or not.
     * @param nextProps
     * @param nextState
     * @returns {boolean}
     */
    shouldComponentUpdate(nextProps, nextState) {
        // TODO: if the deep isEqual check appears to be affecting performance,
        // we may need to remove this method.  However my initial timing tests
        // were showing 0 to 4 ms of time to compare, so I don't think this is an issue.
        if (_.isEqual(this.props, nextProps) && _.isEqual(this.state, nextState)) {
            return false;
        }
        return true;
    }

    componentWillReceiveProps(nextProps) {

        if (nextProps.revisionNum) {
            if (nextProps.revisionNum !== this.props.revisionNum || nextProps.processId !== this.props.processId) {
                ProcessService.fetchProcessRevisionIfNeeded(nextProps.processId, nextProps.revisionNum);
            }
        } else {
            if (nextProps.processId !== this.props.processId) {
                const mode = this.getModeFromPath(nextProps);
                ProcessService.fetchProcessIfNeeded(nextProps.processId, { mode });
            }
        }

        if (nextProps.process && this.props.userLoggedIn === false && nextProps.userLoggedIn === true) {
            // We have to explore datastreams if needed
            const datastreamsToExplore = DataExplorerService.getDatastreamsThatNeedExploring(nextProps.process);
            if (!_.isEmpty(datastreamsToExplore)) {
                ProcessUtils.callDataExplorerWebService(nextProps.process, datastreamsToExplore);
            }
        }

        const pathMode = this.getModeFromPath(nextProps);
        if (nextProps.process && pathMode === 'wizard' && !ProcessUtils.canOpenInWizardMode(nextProps.process)) {

            const modalId = `wizard_redirect_${nextProps.processId}`;
            let redirectModalExists = false;
            ModalsService.getMyState().openModals.forEach(modal => {
                if (modal.id === modalId) {
                    redirectModalExists = true;
                }
            });

            if (!redirectModalExists) {
                ModalsService.openModal({
                    id: modalId,
                    modalType: ModalsService.TYPE_INFO,
                    modalTitle: 'Wizard Not Allowed',
                    modalMessage: ProcessUtils.getWizardModeDisabledMessage(nextProps.process)
                });
            }
            NavigationUtils.gotoProcess(nextProps.processId);
        }

        if (nextProps.mode !== null && nextProps.mode !== pathMode) {
            ProcessService.updateProcessUIState(nextProps.processId, { mode: pathMode });
        }
    }

    getModeFromPath(props) {
        const segments = NavigationUtils.getPathSegments(props.location.pathname);
        let mode = 'standard';
        if (segments.length > 2 && (segments[2] === 'wizard' || segments[2] === 'text')) {
            mode = segments[2];
        }
        return mode;
    }

    getNavClickFunction(relativePath) {
        return NavigationUtils.getNavClickFunction(this.props.processId, this.props.revisionNum, relativePath);
    }

    saveProcess = () => {
        ProcessService.saveProcess(this.props.processId, this.props.revisionNum);
    };

    saveAsNewProcess = () => {
        ProcessUtils.saveAsNewProcess(this.props.process, this.props.revisionNum);
    };

    /**
     * Check if the entity specified by the route exists
     * @param details
     * @returns {boolean}
     */
    checkEntityExists = (details) => {
        let exists = true;

        // parse out the path segments
        const segments = NavigationUtils.getPathSegments(this.props.location.pathname);

        if (segments.length > 3) {
            const entity = segments[4];
            let entityLabel = '';

            switch (segments[3]) {
                case 'coordinate-system':
                    entityLabel = 'Coordinate System';
                    exists = !!_.get(this.props.process, ['variable_retrieval', 'coordinate_systems', entity]);
                    break;
                case 'retrieved-variable':
                    entityLabel = 'Variable';
                    exists = !!_.get(this.props.process, ['variable_retrieval', 'retrieved_variables', entity]);
                    break;
                case 'output-datastream':
                    entityLabel = 'Output Datastream';
                    exists = _.includes(this.props.process.output_datastreams, entity);
                    break;
                default:  // stupid linting rule requires a default case
                    break;
            }
            details.entity = `${entityLabel} '${entity}'`;
        }
        return exists;
    };

    isOverviewTable() {
        return typeof(this.getLSState().isOverviewTable) !== 'undefined' ? this.getLSState().isOverviewTable : false;
    }

    /**
     * Render process navigation that lets you  switch between forms of the process.
     */
    renderNav() {
        // get the rollup errors for Process and Inputs & Outputs level 1 navigation

        let inputsAndOutputs = null;
        let runDataConsolidator = '';
        let createAdiProject = '';
        if (_.isEmpty(this.props.process.variable_retrieval)) {
            inputsAndOutputs = this.renderNavItem('2: Define Inputs & Outputs', '/inputs-outputs', ['input-outputs']);

        } else {
            inputsAndOutputs = this.renderVAPInputsAndOutputs();
            runDataConsolidator = this.renderNavItem('3: Run Data Consolidator', '/run-code');
            createAdiProject = this.renderNavItem('4: Create ADI Project', '/create-adi-project');
        }

        return (
            <Flexbox flexDirection="column" className="left-nav" minWidth="240px">
                {this.renderNavItem('1: Describe Process', '/', ['process-info'])}
                {inputsAndOutputs}
                {runDataConsolidator}
                {createAdiProject}
                {this.renderNavItem('Revision History', '/revision-history')}
            </Flexbox>
        );
    }

    renderVAPInputsAndOutputs() {
        // parse out the path segments
        const segments = NavigationUtils.getPathSegments(this.props.location.pathname);
        let overviewEntity, coordinateSystemEntity, variableEntity, outputDatastreamEntity, retrievalRulesEntity, outputQCEntity, inputQCEntity = null;
        const baseCount = this.props.revisionNum ? 5 : 3;
        let isActive = false;
        const overviewPath = this.isOverviewTable() ? '/variable-retrieval/spreadsheet' : '/variable-retrieval';

        if (segments.length > baseCount) {
            isActive = true;
            switch (segments[baseCount]) {
                case 'coordinate-system':
                    coordinateSystemEntity = `${segments[baseCount + 1]}: ${segments[baseCount + 2]}`;
                    break;
                case 'retrieved-variable':
                    variableEntity = segments[baseCount + 1];
                    break;
                case 'input-qc':
                    inputQCEntity = 'input-qc';
                    break;
                case 'output-datastream':
                    outputDatastreamEntity = segments[baseCount + 1];
                    break;
                case 'output-qc':
                    if (segments[baseCount + 1]) {
                        outputQCEntity = `${segments[baseCount + 1]}: ${segments[baseCount + 2]}`;
                    } else {
                        outputQCEntity = 'output-qc';
                    }
                    break;
                case 'retrieval-rules':
                    retrievalRulesEntity = 'retrieval-rules';
                    break;
                case 'spreadsheet':
                    overviewEntity = 'Overview';
                    break;
                default:  // stupid linting rule requires a default case
                    break;
            }

        } else if (segments.length === baseCount && segments[baseCount - 1] === 'variable-retrieval') {
            overviewEntity = 'Overview';
            isActive = true;
        }

        let navComponent = null;
        let dimensionCount = 0;

        if (isActive) {
            const inputsChildren = [
                this.renderSecondLevelNav('Variables', this.getRetrievedVariableDropdownOptions(), variableEntity, ['variable_retrieval', 'retrieved_variables'], null, 'third-level', null),
                this.renderSecondLevelNav('Retrieval Rules', null, retrievalRulesEntity, ['variable_retrieval', 'input_datasets'], '/variable-retrieval/retrieval-rules', 'third-level', null),
            ];
            const coordinateChildren = [];
            const outputsChildren = [
                this.renderSecondLevelNav('Output Datastreams', this.getOutputDatastreamDropdownOptions(), outputDatastreamEntity, ['output_datastreams'], null, 'third-level', null),
            ];

            // only include input qc if input data exists
            if (Object.keys(this.props.process.variable_retrieval.retrieved_variables).length > 0) {
                inputsChildren.push(this.renderSecondLevelNav('Input QC', null, inputQCEntity, ['input_qc'], '/variable-retrieval/input-qc', 'third-level', null));

                //only include advanced transformation and output qc if input data and coordinate systems exist
                if (Object.keys(this.props.process.variable_retrieval.coordinate_systems).length > 0) {

                    ProcessUtils.visitDimensions(this.props.process, (dimension, coordinateSystemName) => {
                        dimensionCount += 1;
                    });

                    ProcessUtils.visitDimensions(this.props.process, (dimension, coordinateSystemName) => {
                        const label = `${coordinateSystemName}: ${dimension.name}`;
                        if (dimensionCount > 5) {
                            coordinateChildren.push(
                                {label, path: `/variable-retrieval/coordinate-system/${coordinateSystemName}/${dimension.name}`, errorPath: ['variable_retrieval', 'coordinate_systems', coordinateSystemName, 'dimensions', dimension.name]}
                            );
                        } else {
                            coordinateChildren.push(this.renderSecondLevelNav(
                                label,
                                null,
                                coordinateSystemEntity === label,
                                ['variable_retrieval', 'coordinate_systems', coordinateSystemName, 'dimensions', dimension.name],
                                `/variable-retrieval/coordinate-system/${coordinateSystemName}/${dimension.name}`,
                                'third-level',
                                null)
                            );
                        }
                    });

                    // if there are no options, nav item link to page with message explaining to output qc to set for process
                    let outputQCOptions = this.getOutputQCDropdownOptions();
                    let outputQCLinkPath = null;
                    if (Object.keys(outputQCOptions).length === 0) {
                        outputQCOptions = null;
                        outputQCLinkPath = '/variable-retrieval/output-qc';
                    }

                    outputsChildren.push(this.renderSecondLevelNav('Output QC', outputQCOptions, outputQCEntity, ['output_qc'], outputQCLinkPath, 'third-level', null));
                }
            }

            navComponent = (
                <Flexbox flexDirection="column" className="process-nav-item active depth-two inputs-outputs">
                    <div className="arrow">&nbsp;</div>
                    <div className={`first-row`}>
                        <RollupError fieldPath={['inputs-outputs']}
                                     processErrors={this.props.processErrors}
                                     process={this.props.process}
                        />
                        2: Define Inputs & Outputs
                    </div>
                    {this.renderSecondLevelNav(
                        'Overview', null, overviewEntity, [], overviewPath,
                        'second-level', null
                    )}
                    {this.renderSecondLevelNav(
                        'Inputs', null, null, [], null,
                        'second-level', inputsChildren
                    )}
                    {dimensionCount > 5 ?
                        this.renderSecondLevelNav(
                        'Coordinate Systems', coordinateChildren, coordinateSystemEntity, ['variable_retrieval', 'coordinate_systems'], null,
                        'second-level', null, true
                        )
                        :
                        this.renderSecondLevelNav(
                        'Coordinate Systems', null, null, [], null,
                        'second-level', coordinateChildren, true
                    )}
                    {this.renderSecondLevelNav(
                        'Outputs', null, null, [], null,
                        'second-level', outputsChildren
                    )}
                </Flexbox>
            );
        } else {
            navComponent = this.renderNavItem('2: Define Inputs & Outputs', overviewPath, ['inputs-outputs']);
        }

        return navComponent;
    }

    renderNewIcon(title, isActive) {
        let newFunc = null;
        if (title === 'Variables') {
            newFunc = ProcessUtils.newRetrievedVariableFunction(this.props.process);

        } else if (title === 'Coordinate Systems') {
            newFunc = ProcessUtils.newCoordinateSystemFunction(this.props.process, null, isActive);

        } else if (title === 'Output Datastreams') {
            newFunc = ProcessUtils.newOutputDatastreamFunction(this.props.process, null, isActive);
        }

        let className = null;
        let onClickFunc = () => {}; // do nothing
        if (this.props.userLoggedIn && this.props.hasWritePermissions) {
            onClickFunc = newFunc;
        } else {
            className = 'disabled';
        }

        let newComponent = null;
        if (newFunc) {
            const iconTitle = `New ${title.substring(0, title.length - 1)}`; // truncate off the 's'
            newComponent = (
                <a className={`new-nav ${className}`} title={iconTitle} onClick={onClickFunc}><i className="fa fa-plus new" /></a>
            );
        }

        return newComponent;
    }

    renderSecondLevelNav(title, entityDropdownOptions, activeEntity, errorPath, linkPath, levelClass, children, newIcon) {
        let navContent = null;
        let activeClass = '';

        if (activeEntity) {
            activeClass = 'active-entity';
        }

        if (activeEntity && entityDropdownOptions) {
            navContent = (
                <span>
                    <div className="entity-header">
                        {`${title}:`}
                        {this.renderNewIcon(title, activeEntity)}
                    </div>
                    {this.renderDropdownNav(activeEntity, entityDropdownOptions, errorPath)}
                </span>
            );

        } else if (entityDropdownOptions) {
            navContent = (
                <span>
                    {this.renderDropdownNav(title, entityDropdownOptions, errorPath)}
                    {this.renderNewIcon(title, activeEntity)}
                </span>
            );

        } else if (newIcon) {
            navContent = (
                <span>
                    {title}
                    {this.renderNewIcon(title, activeEntity)}
                </span>
            );

        } else if (activeEntity || linkPath === null) {
            navContent = title;

        } else {
            navContent = (
                <a onClick={this.getNavClickFunction(linkPath)} title={title}>{title}</a>
            );
        }

        return (
            <React.Fragment key={`${title}`}>
                <div className={`${levelClass} ${activeClass}`}>
                    <RollupError
                        fieldPath={errorPath}
                        processErrors={this.props.processErrors}
                        process={this.props.process}
                        includeChildren
                    />
                    {navContent}
                </div>
                {children &&
                <div className="children">
                    {children}
                </div>
                }
            </React.Fragment>
        );
    }

    renderDropdownNav(title, dropdownOptions, errorPath) {
        let menuItems = null;

        const renderMenuItems = (options) => {
            return options.map(item => {
                const fieldPath = item.errorPath ? item.errorPath : [...errorPath, item.label];
                return (
                    <Menu.Item key={item.label} onClick={this.getNavClickFunction(item.path)}>
                        <span>
                            <RollupError fieldPath={fieldPath} processErrors={this.props.processErrors} process={this.props.process} includeChildren/>
                            {item.label}
                        </span>
                    </Menu.Item>
                );
            });
        };

        if (Array.isArray(dropdownOptions)) {
            menuItems = renderMenuItems(dropdownOptions);
        } else {
            menuItems = Object.keys(dropdownOptions).map(groupName => {
                const menuSubItems = renderMenuItems(dropdownOptions[groupName]);

                return (
                    <MenuItemGroup title={groupName} key={groupName} className="grouped-menu">
                        {menuSubItems}
                    </MenuItemGroup>
                );
            });
        }

        const menu = (
            <Menu title={title} id={`${title}-nav-dropdown`}>
                {menuItems}
            </Menu>
        );

        return (
            <Dropdown overlay={menu} trigger={['click']} overlayClassName="secondary-nav-dropdown">
                <span className="ant-dropdown-link">
                    {title} <Icon type="down" />
                </span>
            </Dropdown>
        );
    }

    renderNavItem(title, relativePath, fieldPath) {
        const currentPath = NavigationUtils.removeTrailingSlash(this.props.location.pathname);
        relativePath = NavigationUtils.removeTrailingSlash(relativePath);
        const navPath = NavigationUtils.getPath(this.props.processId, this.props.revisionNum, relativePath);

        const isActive = (currentPath === navPath);
        let navComponent = null;
        const rollupError =  (
            <React.Fragment>
                <RollupError
                    fieldPath={fieldPath}
                    processErrors={this.props.processErrors}
                    process={this.props.process}
                />
                {title}
            </React.Fragment>
        );

        const className = isActive ? 'process-nav-item active' : 'process-nav-item';
        if (!isActive) {
            navComponent = (
                <a className={className} onClick={this.getNavClickFunction(relativePath)} title={title}>
                    {rollupError}
                </a>
            );
        } else {
            navComponent = (
                <div className={className}>
                    {rollupError}
                    <div className="arrow">&nbsp;</div>
                </div>
            );
        }

        return navComponent;
    }

    getEntityDropdownOptions(entityList, basePath) {
        const options = entityList.map((entityName) => {
            return { label: entityName, path: `${basePath}/${entityName}` };
        });
        return options;
    }

    getInputDatasetDropdownOptions() {
        return this.getEntityDropdownOptions(Object.keys(this.props.process.variable_retrieval.input_datasets),
            '/variable-retrieval/input-dataset');
    }

    getOutputDatastreamDropdownOptions() {
        return this.getEntityDropdownOptions(this.props.process.output_datastreams,
            '/variable-retrieval/output-datastream');
    }

    getRetrievedVariableDropdownOptions() {
        return this.getEntityDropdownOptions(Object.keys(this.props.process.variable_retrieval.retrieved_variables),
            '/variable-retrieval/retrieved-variable');
    }

    getCoordinateSystemDropdownOptions() {
        return this.getEntityDropdownOptions(Object.keys(this.props.process.variable_retrieval.coordinate_systems),
            '/variable-retrieval/coordinate-system');
    }

    getTransformParameterDropdownOptions(parameterNames, basePath) {
        const options = {};

        ProcessUtils.visitDimensions(this.props.process, (dimension, coordinateSystemName) => {
            let shouldShow = false;
            parameterNames.forEach(name => {

                shouldShow = shouldShow ||
                    ProcessUtils.isRelevantTransformationParameter(this.props.process, name, [coordinateSystemName], dimension.name, this.props.exploredDatastreams);

            });

            if (shouldShow) {
                const item = { label: dimension.name, path: `${basePath}/${coordinateSystemName}/${dimension.name}` };
                if (!options[coordinateSystemName]) {
                    options[coordinateSystemName] = [item];
                } else {
                    options[coordinateSystemName] = [...options[coordinateSystemName], item];
                }
            }
        });

        return options;
    }

    getOutputQCDropdownOptions = () => {
        const parameterNames = ['std_ind_max', 'std_bad_max', 'goodfrac_ind_min', 'goodfrac_bad_min'];
        const basePath = '/variable-retrieval/output-qc';

        return this.getTransformParameterDropdownOptions(parameterNames, basePath);
    };

    getTransformationDropdownOptions = () => {
        const parameterNames = ['transformation_type'];
        const basePath = '/variable-retrieval/transformations';

        return this.getTransformParameterDropdownOptions(parameterNames, basePath);
    };

    /**
     * Return a unique id for the given page so its state can be saved to local storage if needed.
     */
    getPageId() {
        // parse out the path segments
        let path = this.props.location.pathname;
        path = path.replace(/\/+$/, ''); // remove trailing slash
        path = path.substring(1); // remove initial slash
        const pageId = path.replace(new RegExp('/', 'g'), '-'); // replace all slash with -
        return pageId;
    }

    renderProcess() {
        let variableRequiredProps = null;
        if (this.props.mode === 'wizard') {
            variableRequiredProps = {
                variableRequired: ProcessUtils.getGlobalVariableProperty(this.props.process, 'variable_required', false),
                retrieveQcVariable: ProcessUtils.getGlobalVariableProperty(this.props.process, 'retrieve_qc_variable', true),
                qcVariableRequired: ProcessUtils.getGlobalVariableProperty(this.props.process, 'qc_variable_required', false),
            };
        }
        let importWiz = '';

        if (ProcessUtils.hasVariableRetrieval(this.props.process) && this.props.showImportWizard) {
            importWiz = (
                <ImportVariablesWizard
                    key="import-wizard"
                    process={this.props.process}
                    dods={this.props.dods}
                    dodsById={this.props.dodsById}
                    siteFacilities={this.props.siteFacilities}
                    recommendedVariables={this.props.recommendedVariables}
                    wizardView={this.props.mode === 'wizard'}
                    {...variableRequiredProps}
                />
            );
        }

        const iconColor = this.props.process.production ? 'green' : 'yellow';
        const iconTooltip = this.props.process.production ? 'Production (DMF)' : 'Development';

        // TODO: make this be a link to the summary list view
        let gotoSummaryButton = null;
        let navSeparator = null;
        if (this.props.process.variable_retrieval && !this.props.location.pathname.endsWith('variable-retrieval')) {
            // Use Font Awesome stacked icons
            const iconComponent = (
                <span className="fa-stack fa-lg">
                    <i className="fa fa-stack-1x fa-th-list fa-flip-horizontal" style={{ top: '1px' }} />
                    <i className="fa fa-stack-lg fa-long-arrow-left" style={{ color: '#00BD70' }} />
                </span>
            );
            gotoSummaryButton = (
                <ToolbarItem
                    title="Go back to to Summary List view"
                    iconComponent={iconComponent}
                    clickHandler={NavigationUtils.gotoVariableRetrievalFunc(this.props.processId, this.props.revisionNum)}
                    enabled
                />
            );
            navSeparator = <ToolbarSeparator style={{ paddingLeft: '0' }} />;
        }

        const saveDropdownRefFunction = (component) => { this.saveDropdownComponent = component; };
        let key = this.props.process.name;
        if (this.props.revisionNum) {
            key += this.props.revisionNum;
        }

        let saveMenuItem = "Save";
        let saveEnabled = false;
        if ((this.props.hasWritePermissions && this.props.processIsDirty) || (this.props.revisionNum && this.props.hasSavePermissions)) {
            // hasWritePermisions is set to false on revision page to make it read only, but they should still be able to save if they have the write permissions
            saveEnabled = true;
        }

        const popoverMenuItemStyle = css`display:inline-block; width:100%;`;
        //perl scrips when trying to save a non-ADI process with no input datastreams
        if (_.isEmpty(this.props.process.variable_retrieval) && _.isEmpty(this.props.process.input_datastreams)) {
            saveEnabled = false;
            saveMenuItem = (
                <Popover
                    content={"Basic process requires at least one input datastream defined to save"}
                    placement="top"
                    overlayClassName={popoverErrorStyle}
                >
                    <span className={popoverMenuItemStyle}>{saveMenuItem}</span>
                </Popover>
            );
        }

        let wizardMenuItem = 'Wizard';
        if (!ProcessUtils.canOpenInWizardMode(this.props.process)) {
            wizardMenuItem = (
                <Popover
                    content={ProcessUtils.getWizardModeDisabledMessage(this.props.process)}
                    placement="top"
                    overlayClassName={popoverErrorStyle}
                >
                    <span className={popoverMenuItemStyle}>{wizardMenuItem}</span>
                </Popover>
            );
        }

        const revisionHistoryIconComponent = (
            <span className="fa-stack fa-lg" style={{ width: '1em' }}>
                <i className="fa fa-stack-1x fa-undo fa-rotate-160" />
                <i className="fa fa-stack-1x fa-square"
                    style={{ color: 'white', fontSize: '.5em', top: '-7px', left: '4px' }} />
                <i className="fa fa-stack-lg fa-pencil"
                    style={{ fontSize: '.7em', position: 'relative', top: '-5px', right: '-7px' }} />
            </span>
        );

        /*let wizardClassName = null;
        if (this.props.mode === 'wizard') {
            wizardClassName = 'wizard-container';
        }*/

        let containerStyles = null;
        if (this.props.mode === 'wizard') {
            containerStyles = css`
                height: calc(100vh - 80px);
                display: flex;
                padding: 0;
                border-top: 1px solid ${colorLightGrey};
            `;
        } else {
            containerStyles = css`
                height: calc(100vh - 140px);
                display: flex;
                overflow: auto;
                padding: 8px 10px 0 20px;
                background-color: ${colorVeryLightGrey};
                border-top: 1px solid ${colorLightGrey};
            `;
        }

        const basePath = this.props.match.path;
        const props = {
            process: this.props.process,
            processId: this.props.processId,
            revisionNum: this.props.revisionNum,
            processErrors: this.props.processErrors,
            processRevisions: this.props.processRevisions,
            exploredDatastreams: this.props.exploredDatastreams,
            dispatch: this.props.dispatch,
            hasWritePermissions: this.props.hasWritePermissions,
            userLoggedIn: this.props.userLoggedIn,
            dods: this.props.dods,
            dodsById: this.props.dodsById,
            siteFacilities: this.props.siteFacilities,
            recommendedVariables: this.props.recommendedVariables,
            id: this.getPageId()
        };

        return (
            <div key={`${key}`} className="process page">
                {/* This is the green bar at the top of the page (still has old classname) */}
                <div className="second-level-nav" />
                <Flexbox flexDirection="column" flexGrow={1}>
                    {this.props.mode !== 'wizard' &&
                    <Flexbox flexDirection="row" justifyContent="space-between" alignItems="center" className={css`height: 50px;`}>
                        <div className="process-title">
                            <i className={`fa fa-cogs ${iconColor}`} aria-hidden="true" title={iconTooltip}/>
                            <span className="process-label">Process:</span>
                            {this.props.process.name}
                        </div>
                        <Toolbar className="main">

                            {gotoSummaryButton}
                            {navSeparator}

                            {/* This is not in old pcm - not sure if we want to implement or not
                             <ToolbarItem title="Import" className="fa fa-lg fa-upload" enabled={false}/>
                             */}
                            {/* This is not in old pcm - not sure if we want to implement or not
                             <ToolbarItem title="Export" className="fa fa-lg fa-download" enabled={false}/>
                             */}
                            <ToolbarItem
                                title={<span className={css`text-transform:capitalize;`}><strong>Mode:</strong> {this.props.mode}</span>}
                                enabled
                                dropdownButton
                                textOnly
                            >
                                <Menu>
                                    <Menu.Item
                                        disabled={!ProcessUtils.canOpenInWizardMode(this.props.process)}
                                        onClick={NavigationUtils.gotoWizardModeFunc(this.props.processId, this.props.revisionNum)}
                                    >
                                        {wizardMenuItem}
                                    </Menu.Item>
                                    <Menu.Item
                                        onClick={NavigationUtils.gotoProcessFunc(this.props.processId, this.props.revisionNum)}
                                    >
                                        Standard
                                    </Menu.Item>
                                    <Menu.Item
                                        onClick={NavigationUtils.gotoTextEditorFunc(this.props.processId, this.props.revisionNum)}
                                    >
                                        Text
                                    </Menu.Item>
                                </Menu>
                            </ToolbarItem>

                            <ToolbarSeparator />
                            <ToolbarItem
                                className="fa fa-lg fa-floppy-o"
                                enabled
                                dropdownButton
                                ref={saveDropdownRefFunction}
                            >
                                <Menu>
                                    <Menu.Item
                                        disabled={!saveEnabled}
                                        onClick={this.saveProcess}
                                    >
                                        {saveMenuItem}
                                    </Menu.Item>
                                    <Menu.Item
                                        disabled={!this.props.userLoggedIn}
                                        onClick={this.saveAsNewProcess}
                                    >
                                        Save As
                                    </Menu.Item>
                                </Menu>
                            </ToolbarItem>
                            <ToolbarSeparator/>
                            <ToolbarItem
                                title="View Revision History"
                                iconComponent={revisionHistoryIconComponent}
                                clickHandler={this.getNavClickFunction('/revision-history')}
                                enabled
                            />
                            <ToolbarSeparator />
                            <ToolbarItem
                                title="Undo (not implemented yet)"
                                className="fa fa-lg fa-undo"
                                enabled={false}
                            />
                            <ToolbarItem
                                title="Redo (not implemented yet)"
                                className="fa fa-lg fa-repeat"
                                enabled={false}
                            />
                            <ToolbarSeparator />
                            <ToolbarItem
                                title="Help (not implemented yet)"
                                className="fa fa-lg fa-question-circle"
                                enabled={false}
                            />
                        </Toolbar>
                    </Flexbox>
                    }
                    <Flexbox flexDirection="row" flexGrow={1}>
                        {this.props.mode !== 'wizard' &&
                            this.renderNav()
                        }
                        {/* Clone the single child so we can pass the process property to them */}
                        {/* Note that this.props.children is an opaque element and could be an array or a single element */}
                        <Flexbox flexDirection="row" className={`${containerStyles} process-form-container rie-scrollable`} flexGrow={1}>
                            <Switch>
                                <Route path={`${basePath}/wizard`} render={(routeProps) => <ProcessWizard {...routeProps} {...props} />} />
                                <Route path={`${basePath}/text`} render={(routeProps) => <ProcessTextEditor {...routeProps} {...props} />} />
                                <Route path={`${basePath}/revision-history`} render={(routeProps) => <RevisionHistory {...routeProps} {...props} />} />
                                <Route path={`${basePath}/inputs-outputs`} render={(routeProps) => <InputsOutputsForm {...routeProps} {...props} />} />
                                <Route path={`${basePath}/run-code`} render={(routeProps) => <RunCodeForm {...routeProps} {...props} />} />
                                <Route path={`${basePath}/create-adi-project`} render={(routeProps) => <CreateCodeForm {...routeProps} {...props} />} />
                                <Route path={`${basePath}/variable-retrieval`} render={(routeProps) => <VariableRetrieval  {...routeProps} {...props} />} />
                                <Route exact path={basePath} render={(routeProps) => <ProcessForm {...routeProps} {...props} />} />
                                <Route exact path={`${basePath}/*`} component={PageDoesNotExist} />} />
                            </Switch>
                        </Flexbox>
                    </Flexbox>
                </Flexbox>

                {importWiz}
            </div>
        );
    }

    render() {
        let component = null;

        if (!this.props.process || !this.props.dodsById || this.props.dods.length === 0) {
            component = <div />;

        } else {
            const details = {};
            const entityExists = this.checkEntityExists(details);
            if (entityExists) {
                component = this.renderProcess();

            } else if (!this.props.processIsDirty) { // if the process is dirty, we are renaming, so we should NOT show this
                // print 404 message
                component = (
                    <Flexbox>
                        <h1>Page Does Not Exist</h1>
                        <p>{details.entity} does not exist.</p>
                        <p>
                            <a onClick={NavigationUtils.gotoProcessFunc(this.props.processId, this.props.revisionNum)}>
                                Go back to process.
                            </a>
                        </p>
                    </Flexbox>
                );
            }
        }
        return component;
    }
}

// Automatically pull state information from the store and
// pass it to this component as props
const mapStateToProps = (storeState, ownProps) => {
    const processId = ownProps.match.params.processId;
    const revisionNum = ownProps.match.params.revisionNum;

    const myState = ProcessService.getMyState(storeState);
    const modalsState = ModalsService.getMyState(storeState);
    const dods = DodService.getMyState(storeState).sortedIds;
    const dodsById = DodService.getMyState(storeState).dodsById;
    const dataExplorerState = DataExplorerService.getMyState(storeState);

    let process = null;
    let processUIState = null;
    let processIsDirty = null;
    let mode = null;
    let showImportWizard = false;
    modalsState.openModals.forEach(modal => {
        if (modal.id === ModalsService.TYPE_IMPORT_WIZARD) {
            showImportWizard = true;
        }
    });
    let processErrors = null;
    let hasSavePermissions = null; //need extra prop to determine if user can save process revision, but leave hasWrite alone because revision should be read only
    let hasWritePermissions = false;

    const loginState = SecurityService.getMyState(storeState);
    const userLoggedIn = loginState.userLoggedIn;

    const siteFacilities = SiteFacilitiesService.getMyState(storeState).siteFacilityCatalog;
    const recommendedVariables = RecommendedDataService.getMyState(storeState).recommendedVariables;
    const exploredDatastreams = {};

    if (myState) {
        if (revisionNum) {
            if (myState.processRevisionsById[processId]) {
                process = myState.processRevisionsById[processId][revisionNum];
            }
            hasWritePermissions = false;
            hasSavePermissions = SecurityService.canCurrentUserEditProcess(processId);
            processIsDirty = false;
            showImportWizard = false;

            if (myState.processRevisionErrorsById[processId]) {
                processErrors = myState.processRevisionErrorsById[processId][revisionNum];
            }

        } else {
            process = myState.workingCopiesById[processId];
            if (myState.processUIStateById[processId]) {
                processUIState = myState.processUIStateById[processId];
                mode = myState.processUIStateById[processId].mode;
                processIsDirty = myState.processUIStateById[processId].dirty;
            }
            if (process) {
                hasWritePermissions = ProcessUtils.canCurrentUserEditProcess(processId, process.deprecated);
            }
            processErrors = myState.processErrorsById[processId];
        }

        // For each input datastream, load the explored datastream data if it exists  in the store.
        const processExploredDatastreams = DataExplorerService.getExploredDatastreams(processId, dataExplorerState);
        if (process && processExploredDatastreams) {
            process.input_datastreams.forEach(inputDatastreamName => {
                if (processExploredDatastreams[inputDatastreamName] && !_.isEmpty(processExploredDatastreams[inputDatastreamName].currentData)) {
                    exploredDatastreams[inputDatastreamName] = processExploredDatastreams[inputDatastreamName];
                }
            });
        }

    }

    return {
        siteFacilities,
        recommendedVariables,
        dods,
        dodsById,
        hasWritePermissions,
        hasSavePermissions,
        userLoggedIn,
        processUIState,
        processIsDirty,
        showImportWizard,
        process, // key is process, value is process
        processErrors,
        processId,
        revisionNum,
        exploredDatastreams,
        mode
    };
};


export default connect(mapStateToProps)(Process);



// WEBPACK FOOTER //
// ./src/components/page/process/Process.js